import React, { useId } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { formatScoringOptionLabel } from './threatAreaScoring';

const LEGACY_TWO = [
    {
        value: 'behaviour',
        label: formatScoringOptionLabel('behaviour', 'Behaviour'),
    },
    {
        value: 'knowledge',
        label: formatScoringOptionLabel('knowledge', 'Knowledge'),
    },
];

/**
 * Normalise stored JSON to a `Select` value present in `options`.
 * @param {string} raw
 * @param {{ value: string, label?: string }[]} [options] — from threat area key behaviours; if omitted, legacy behaviour/knowledge only.
 */
export function normalizeScoringComponentToken(raw, options) {
    const t = String(raw ?? '')
        .trim()
        .toLowerCase();
    if (Array.isArray(options) && options.length) {
        const hit = options.find(
            (o) => String(o.value).toLowerCase() === t
        );
        return hit ? hit.value : options[0].value;
    }
    return t === 'knowledge' ? 'knowledge' : 'behaviour';
}

/**
 * Score entry `component` (stored lowercase in definitions).
 * @param {string} value
 * @param {{ value: string, label?: string }[]} [componentOptions] — threat-area key behaviours; if omitted, behaviour | knowledge only.
 */
export function ScoringComponentSelect({
    value,
    onChange,
    componentOptions,
    size = 'small',
    fullWidth = true,
    label = 'Component',
    disabled = false,
}) {
    const lid = useId();
    const items =
        Array.isArray(componentOptions) && componentOptions.length
            ? componentOptions
            : LEGACY_TWO;
    const v = normalizeScoringComponentToken(value, items);
    const displayLabel =
        items.find((o) => o.value === v)?.label ?? items.find((o) => String(o.value).toLowerCase() === String(v).toLowerCase())?.label ?? v;
    return (
        <FormControl
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            sx={{
                minWidth: 0,
                maxWidth: '100%',
                width: fullWidth ? '100%' : undefined,
            }}
        >
            <InputLabel id={lid}>{label}</InputLabel>
            <Select
                labelId={lid}
                label={label}
                value={v}
                onChange={(e) => onChange(e.target.value)}
                renderValue={() => (
                    <Box
                        component="span"
                        sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                        }}
                    >
                        {displayLabel}
                    </Box>
                )}
                sx={{
                    maxWidth: '100%',
                    minWidth: 0,
                    '& .MuiSelect-select': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                    },
                }}
            >
                {items.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                        {o.label ?? o.value}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
